import { useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Typography,
} from '@mui/material';
import { useNotify, useRefresh } from 'react-admin';

import { ContractorOwnerAssignControl } from '@/components/ContractorOwnerAssignControl';
import { ContractorOwner, QuoteJobFolder } from '@/types';
import { updateProjectContacts } from '@/utils/contractor_owner_utils';

type EditProjectContactsDialogProps = {
    open: boolean;
    folder: QuoteJobFolder;
    onClose: () => void;
};

function personFrom(
    id: number | null | undefined,
    name: string | null | undefined,
): ContractorOwner | null {
    if (!id) return null;
    return {
        id,
        name: name || '',
        company_name: '',
        email: '',
        phone_number: '',
    };
}

export const EditProjectContactsDialog = ({
    open,
    folder,
    onClose,
}: EditProjectContactsDialogProps) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [owner, setOwner] = useState<ContractorOwner | null>(null);
    const [contractor, setContractor] = useState<ContractorOwner | null>(null);
    const [useAsContractor, setUseAsContractor] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const nextOwner = personFrom(folder.property_owner_id, folder.property_owner_name);
        const nextContractor = personFrom(folder.contractor_id, folder.contractor_name);
        setOwner(nextOwner);
        setContractor(nextContractor);
        setUseAsContractor(
            !folder.contractor_id ||
                Number(folder.contractor_id) === Number(folder.property_owner_id),
        );
    }, [
        open,
        folder.property_owner_id,
        folder.property_owner_name,
        folder.contractor_id,
        folder.contractor_name,
    ]);

    const handleSave = async () => {
        if (!owner || Number(owner.id) <= 0) {
            notify('Select an owner first', { type: 'warning' });
            return;
        }
        const contractorId = useAsContractor
            ? Number(owner.id)
            : Number(contractor?.id ?? 0);
        if (!useAsContractor && contractorId <= 0) {
            notify('Select a contractor first', { type: 'warning' });
            return;
        }
        const ownerId = Number(owner.id);
        if (
            ownerId === Number(folder.property_owner_id) &&
            contractorId === Number(folder.contractor_id)
        ) {
            onClose();
            return;
        }

        setIsSaving(true);
        try {
            await updateProjectContacts(Number(folder.id), {
                property_owner_id: ownerId,
                contractor_id: contractorId,
            });
            notify('Owner & contractor updated', { type: 'success' });
            refresh();
            onClose();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to update contacts', {
                type: 'error',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Transfer owner & contractor</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} pt={1}>
                    <Typography variant="body2" color="text.secondary">
                        Set who owns the property now, and optionally a separate contractor.
                        Anyone you replace stays on the job as a related person.
                    </Typography>
                    <ContractorOwnerAssignControl
                        label="Owner"
                        value={owner}
                        onChange={setOwner}
                        disabled={isSaving}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={useAsContractor}
                                onChange={(_, checked) => setUseAsContractor(checked)}
                                disabled={isSaving}
                            />
                        }
                        label="Use owner as contractor"
                    />
                    {!useAsContractor && (
                        <ContractorOwnerAssignControl
                            label="Contractor"
                            value={contractor}
                            onChange={setContractor}
                            disabled={isSaving}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={isSaving}>
                    {isSaving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** @deprecated Use EditProjectContactsDialog */
export const EditProjectContractorDialog = EditProjectContactsDialog;
